import React, { useMemo, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useEa } from "@/components/energy-analytics/analytics/EaContext";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaField, EaNote, EaPageHeader, EaSeg } from "@/components/energy-analytics/analytics/EaUi";
import { EaExportButton, EaPointPicker, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { COLORS, INDEX_NOTE, fmtDay, fmtEur, fmtPower, fmtQty, fmtSignedPct, missingMsg } from "@/components/energy-analytics/analytics/eaFormat";
import { toIndex } from "./EaPowerComparePoints";
import { EaDateInput } from "@/components/energy-analytics/analytics/EaDateInput";

const iso = (d) => d.toISOString().slice(0, 10);
const monthRange = (y, m) => [iso(new Date(Date.UTC(y, m - 1, 1))), iso(new Date(Date.UTC(y, m, 0)))];

function presetRanges(key, last) {
  const [y, m] = [Number(last.slice(0, 4)), Number(last.slice(5, 7))];
  if (key === "mom") return [monthRange(y, m), monthRange(m === 1 ? y - 1 : y, m === 1 ? 12 : m - 1)];
  if (key === "yoy_month") return [monthRange(y, m), monthRange(y - 1, m)];
  return [[`${y}-01-01`, monthRange(y, m)[1]], [`${y - 1}-01-01`, monthRange(y - 1, m)[1]]];
}

const METRICS = [
  ["consumption", "Consumo", (v, u) => fmtPower(v, u)], ["spend", "Spesa fatturata", (v) => fmtEur(v)], ["avg_cost", "Costo medio", (v) => fmtQty(v, 2, "€/MWh")],
  ["pun_weighted", "PUN ponderato", (v) => fmtQty(v, 2, "€/MWh")], ["peak_kw", "Picco", (v) => fmtQty(v, 0, "kW")], ["theoretical_cost", "Costo teorico PUN", (v) => fmtEur(v)],
];

function RangeInputs({ r, set, id, onCustom }) {
  return (
    <div className="flex items-center gap-1">
      <EaDateInput value={r[0]} onChange={(v) => { onCustom(); set([v, r[1]]); }} testid={`ea-per-${id}-from`} />
      <span className="text-slate-400">→</span>
      <EaDateInput value={r[1]} onChange={(v) => { onCustom(); set([r[0], v]); }} testid={`ea-per-${id}-to`} />
    </div>
  );
}

export function deltaWhy(d, k, labs) {
  const nulls = ["a", "b"].filter((s) => d[s][k] == null);
  const side = nulls.find((s) => d[s].missing_points?.length) || nulls[0] || "b";
  const per = d[side], P = `periodo ${side.toUpperCase()} (${labs[side]})`;
  if (per.missing_points?.length) return missingMsg(per.missing_points, P);
  if ((k === "spend" || k === "avg_cost") && per.full_months === 0) return `Non calcolabile: il ${P} non contiene mesi interi.`;
  if ((k === "spend" || k === "avg_cost") && per.invoiced_months < per.full_months) return `Non calcolabile: in database mancano le fatture del ${P}.`;
  if (k === "pun_weighted" || k === "theoretical_cost") return `Non calcolabile: in database mancano il PUN o le curve quartorarie del ${P}.`;
  return missingMsg([], P);
}

function DeltaCard({ k, label, fmt, d, unit, labs }) {
  const dl = d.delta[k];
  const up = dl.abs != null && dl.abs > 0;
  return (
    <div className="card-elevated p-4" data-testid={`ea-per-kpi-${k}`} data-pdf="kpi">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-pdf-label>{label}</div>
      <div className="grid grid-cols-2 gap-2 mt-2 font-mono-nums" data-pdf-value>
        <div><div className="text-[10px] text-slate-400">A</div><div className="font-semibold text-slate-900">{fmt(d.a[k], unit)}</div></div>
        <div><div className="text-[10px] text-slate-400">B</div><div className="font-semibold text-slate-600">{fmt(d.b[k], unit)}</div></div>
      </div>
      <div data-pdf-sub>
      <div className={`mt-2 text-sm font-semibold ${dl.abs == null ? "text-slate-400" : up ? "text-red-600" : "text-green-700"}`}>
        {dl.abs == null ? "Δ non calcolabile" : `Δ ${up ? "+" : ""}${fmt(dl.abs, unit)} · ${fmtSignedPct(dl.pct)}`}
      </div>
      {dl.abs == null && <div className="mt-1 text-xs text-slate-500" data-testid={`ea-per-kpi-${k}-why`}>{deltaWhy(d, k, labs)}</div>}
      </div>
    </div>
  );
}

export default function EaPowerComparePeriods() {
  const { nav, unit } = useEa();
  const codes = useSelectedCodes("POWER");
  const last = nav?.availability?.last_curve_date || `${nav?.year}-12-31`;
  const [preset, setPreset] = useState("mom");
  const init = presetRanges("mom", last);
  const [ra, setRa] = useState(init[0]);
  const [rb, setRb] = useState(init[1]);
  const [mode, setMode] = useState("abs");
  const pick = (k) => { setPreset(k); if (k !== "custom") { const [a, b] = presetRanges(k, last); setRa(a); setRb(b); } };
  const { data: d, loading, error } = useEaData("/ea/power/compare/periods", { codes, a_from: ra[0], a_to: ra[1], b_from: rb[0], b_to: rb[1] });
  const chart = useMemo(() => {
    if (!d) return [];
    const n = Math.max(d.a.daily.length, d.b.daily.length);
    const a = d.a.daily.map((x) => x.kwh), b = d.b.daily.map((x) => x.kwh);
    const [ai, bi] = mode === "index" ? [toIndex(a), toIndex(b)] : [a, b];
    return Array.from({ length: n }, (_, i) => ({ g: i + 1, A: ai[i] ?? null, B: bi[i] ?? null, da: d.a.daily[i]?.date, db: d.b.daily[i]?.date }));
  }, [d, mode]);
  const lab = (r) => `${fmtDay(r[0])} – ${fmtDay(r[1])}`;
  return (
    <div className="space-y-6" data-testid="ea-power-compare-periods">
      <EaPageHeader title="Confronto periodi" subtitle="Mese vs mese, anno vs anno o periodi personalizzati. La spesa è confrontata solo sui mesi interi fatturati." right={<EaPointPicker commodity="POWER" />} />
      <div className="card-elevated p-4 flex flex-wrap items-end gap-4" data-testid="ea-per-controls">
        <EaField label="Confronto rapido"><EaSeg value={preset} onChange={pick} options={[["mom", "Mese vs mese prec."], ["yoy_month", "Mese vs anno prec."], ["ytd", "Anno vs anno"], ["custom", "Personalizzato"]]} testid="ea-per-preset" /></EaField>
        <EaField label="Periodo A"><RangeInputs r={ra} set={setRa} id="a" onCustom={() => setPreset("custom")} /></EaField>
        <EaField label="Periodo B"><RangeInputs r={rb} set={setRb} id="b" onCustom={() => setPreset("custom")} /></EaField>
      </div>
      {loading ? <div className="text-sm text-slate-400" data-testid="ea-loading">Caricamento…</div> : error ? <EaNote testid="ea-per-error">{error}</EaNote> : d && <>
        <div className="text-sm text-slate-600" data-testid="ea-per-labels" data-pdf-filter><b>A</b> {lab(ra)} ({d.a.days} giorni) · <b>B</b> {lab(rb)} ({d.b.days} giorni)</div>
        {["a", "b"].map((s) => !!d[s].missing_points?.length && <EaNote key={s} testid={`ea-per-${s}-missing`}>{missingMsg(d[s].missing_points, `periodo ${s.toUpperCase()} (${s === "a" ? lab(ra) : lab(rb)})`)}</EaNote>)}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {METRICS.map(([k, l, f]) => <DeltaCard key={k} k={k} label={l} fmt={f} d={d} unit={unit} labs={{ a: lab(ra), b: lab(rb) }} />)}
        </div>
        <EaNote>Spesa e costo medio: A {d.a.invoiced_months}/{d.a.full_months} mesi interi fatturati · B {d.b.invoiced_months}/{d.b.full_months}. Δ rosso = aumento.</EaNote>
        <EaChartCard title="Consumo giornaliero A vs B" subtitle="Giorni allineati per posizione (1° giorno con 1° giorno)" testid="ea-per-card-chart"
          right={<div className="flex gap-2 flex-wrap"><EaSeg value={mode} onChange={setMode} options={[["abs", "kWh"], ["index", "Indice base 100"]]} testid="ea-per-mode" />
            <EaExportButton filename="power_confronto_periodi" commodity="POWER" period={`A ${lab(ra)} vs B ${lab(rb)}`} testid="ea-per-export"
              columns={[{ key: "g", label: "Giorno n." }, { key: "da", label: "Data A" }, { key: "A", label: mode === "index" ? "A indice" : "A kWh" }, { key: "db", label: "Data B" }, { key: "B", label: mode === "index" ? "B indice" : "B kWh" }]}
              rows={chart.map((r) => ({ ...r, da: fmtDay(r.da), db: fmtDay(r.db) }))} /></div>}>
          <div className="h-72" data-testid="ea-per-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="g" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => fmtQty(v, 0)} />
                <Tooltip formatter={(v, n, p) => [mode === "index" ? fmtQty(v, 1) : fmtQty(v, 0, "kWh"), `${n} ${fmtDay(n === "A" ? p.payload.da : p.payload.db)}`]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line dataKey="A" stroke={COLORS.power} strokeWidth={2} dot={false} />
                <Line dataKey="B" stroke="#475569" strokeWidth={2} strokeDasharray="5 3" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3"><EaNote testid="ea-per-index-note">{INDEX_NOTE} Nel grafico la media è quella giornaliera di ciascun periodo.</EaNote></div>
        </EaChartCard>
      </>}
    </div>
  );
}
